import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';

// components
import {
    ButtonPrimaryLarge,
    ButtonPrimarySmall,
    H4,
    BodyMedium
} from '@styled';

// functionality
import { mediaQueries } from '@general/breakpoints';
import colors from '@general/colors';
import { useScreenMinMd } from '@hooks/useResponsive';

export const modalStyles = {
    body: {
        paddingBottom: '20px',
        paddingTop: '10px',
    },
    content: {
        maxWidth: 'auto',
        minWidth: 'auto',
        width: '594px',
    },
    overlay: {},
};

const BodyContainerStyled = styled.div`
    padding-left: 43px;
    padding-right: 43px;
`;

const ButtonContainerStyled = styled.div`
    border-top: 1px solid ${colors.lightGray};
    bottom: 20px;
    display: flex;
    justify-content: flex-end;
    margin-top: 50px;
    padding-left: 24px;
    padding-right: 24px;
    padding-top: 20px;
    position: absolute;
    width: 100%;

    @media ${mediaQueries.md} {
        position: static;
    }
`;

const MessageContainerStyled = styled.div`
    margin-top: 1.5rem;
    margin-bottom: 1.5rem;
`;

const EventListStyled = styled.ul`
    list-style: none;
    padding: 0;
    margin-top: 2rem;
    margin-bottom: 1.5rem;

    ${({ singleColumn, centered }) => singleColumn ? `
        text-align: center;
    ` : `
        columns: 2;
        column-gap: 1.5rem;
        ${centered ? 'text-align: center;' : ''}
    `}

    li {
        margin-bottom: 12px;
        font-weight: 600;
    }
`;

const FinePrintStyled = styled.p`
    font-size: 11px;
    font-weight: 400;
    line-height: 18px;
    margin-top: 25px;
    background-color: ${colors.ultraLightGray};
    padding: 5px;
`;

export const EventLinkStyled = styled.a`
    color: ${colors.azure};
    text-decoration: none;
    transition: color 0.2s ease-in-out;

    &:hover {
        color: #0051f0;
        text-decoration: underline;
    }
`;


const DEALMAKING_FINE_PRINT =
    '* Offer valid for select ICSC Members in good standing to attend one (1) of the eligible ICSC@ dealmaking events through February 28, 2027. Limit one (1) single-use discount per person. Offer is non-transferable. Additional terms and conditions may apply.';

const DEFAULT_CHECKOUT_TEXT = 'Register today and your discount will be applied automatically at check out.';

const DealMakingModalBase = ({ heading, message, events, separatorAfter, finePrint, checkoutText, onClose }) => {
    const isScreenMinMd = useScreenMinMd();

    return (
        <div>
            <BodyContainerStyled>
                <H4>{heading}</H4>

                <MessageContainerStyled>
                    <BodyMedium>{message}</BodyMedium>
                    {events.length > 0 && (
                        separatorAfter ? (
                            <>
                                <EventListStyled singleColumn>
                                    {events.slice(0, separatorAfter).map(event => (
                                        <li key={event.code}>
                                            <EventLinkStyled href={`/${event.code}`} onClick={onClose}>
                                                {event.title}
                                            </EventLinkStyled>
                                        </li>
                                    ))}
                                </EventListStyled>
                                <BodyMedium style={{ textAlign: 'center', margin: '0.25rem 0' }}>and/or</BodyMedium>
                                <EventListStyled centered>
                                    {events.slice(separatorAfter).map(event => (
                                        <li key={event.code}>
                                            <EventLinkStyled href={`/${event.code}`} onClick={onClose}>
                                                {event.title}
                                            </EventLinkStyled>
                                        </li>
                                    ))}
                                </EventListStyled>
                            </>
                        ) : (
                            <EventListStyled>
                                {events.map(event => (
                                    <li key={event.code}>
                                        <EventLinkStyled href={`/${event.code}`} onClick={onClose}>
                                            {event.title}
                                        </EventLinkStyled>
                                    </li>
                                ))}
                            </EventListStyled>
                        )
                    )}
                    <BodyMedium className="mt-1rem">
                        {checkoutText || DEFAULT_CHECKOUT_TEXT}
                    </BodyMedium>
                    <FinePrintStyled>
                        {finePrint || DEALMAKING_FINE_PRINT}
                    </FinePrintStyled>
                </MessageContainerStyled>
            </BodyContainerStyled>

            <ButtonContainerStyled>
                {isScreenMinMd ? (
                    <ButtonPrimaryLarge onClick={onClose}>GOT IT</ButtonPrimaryLarge>
                ) : (
                    <ButtonPrimarySmall onClick={onClose}>GOT IT</ButtonPrimarySmall>
                )}
            </ButtonContainerStyled>
        </div>
    );
};

DealMakingModalBase.propTypes = {
    heading: PropTypes.string.isRequired,
    message: PropTypes.node.isRequired,
    events: PropTypes.arrayOf(
        PropTypes.shape({
            code: PropTypes.string,
            title: PropTypes.string,
        })
    ).isRequired,
    separatorAfter: PropTypes.number,
    finePrint: PropTypes.string,
    checkoutText: PropTypes.string,
    onClose: PropTypes.func.isRequired,
};

export default DealMakingModalBase;
